import React, { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { cn } from '@/lib/utils';
import { acquirePdf, pdfPageThumbnail, thumbnailWidth } from '@/lib/pdfjs';

interface PdfThumbnailProps {
  url: string;
  doc: PDFDocumentProxy | null;
  pageNumber: number;
  /** Pixel width to render at (use thumbnailWidth to bucket it). */
  renderWidth: number;
  alt?: string;
  className?: string;
}

/** One rendered page as an image; a soft paper skeleton until it's ready. */
export function PdfThumbnail({ url, doc, pageNumber, renderWidth, alt = '', className }: PdfThumbnailProps) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    pdfPageThumbnail(url, doc, pageNumber, renderWidth)
      .then((objectUrl) => { if (!cancelled) setSrc(objectUrl); })
      .catch((err) => console.error('[PdfThumbnail] Page render failed', err));
    return () => { cancelled = true; };
  }, [url, doc, pageNumber, renderWidth]);

  return (
    <div className={cn('absolute inset-0 bg-white', className)}>
      {src ? (
        <img src={src} alt={alt} className="w-full h-full object-cover animate-in fade-in-0 duration-300" draggable={false} decoding="async" />
      ) : (
        <PaperSkeleton />
      )}
    </div>
  );
}

/**
 * The first page of the PDF at `url`, loaded once it's near the screen.
 * Fills its (positioned) parent; skeleton lines until ready or if it fails.
 */
export function PdfCover({ url, cssWidth, alt = '' }: { url: string; cssWidth: number; alt?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setNear(true); },
      { rootMargin: '400px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near || !url) return;
    let cancelled = false;
    const { promise, release } = acquirePdf(url);
    promise
      .then((pdf) => { if (!cancelled) setDoc(pdf); })
      .catch((err) => console.error('[PdfCover] Failed to load PDF', err));
    return () => {
      cancelled = true;
      setDoc(null);
      release();
    };
  }, [url, near]);

  return (
    <div ref={ref} className="absolute inset-0">
      {doc ? (
        <PdfThumbnail url={url} doc={doc} pageNumber={1} renderWidth={thumbnailWidth(cssWidth)} alt={alt} className="[&>img]:object-top" />
      ) : (
        <PaperSkeleton pulse={near} />
      )}
    </div>
  );
}

/** Placeholder lines on white: reads as "a page" while it renders or if it can't. */
export function PaperSkeleton({ pulse = true }: { pulse?: boolean }) {
  return (
    <div className={cn('absolute inset-0 flex flex-col gap-[7%] p-[12%] bg-white', pulse && 'animate-pulse')} aria-hidden>
      <div className="h-[4%] w-3/4 rounded-sm bg-neutral-200" />
      <div className="h-[3%] w-full rounded-sm bg-neutral-100" />
      <div className="h-[3%] w-5/6 rounded-sm bg-neutral-100" />
      <div className="h-[3%] w-full rounded-sm bg-neutral-100" />
      <div className="h-[3%] w-2/3 rounded-sm bg-neutral-100" />
    </div>
  );
}
